import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '~/Components/Global/Loading/Spinner';
import { useMountEffect, guid } from '~/util';
import { paddleApi, UserApi } from '~/Providers/Api';
import { usePaddleCheckout } from '~/util/usePaddleCheckout';
import { error as showError } from '~/Redux/Modules/Notifications';
import { setUser } from '~/Redux/Modules/User';
import { getPricing } from '~/Config/Stripe';
import Storage from '~/Providers/Storage';
import PaddleTotalWithTax from '~/Components/Global/Paddle/PaddleTotalWithTax';
import moment from '~/Providers/Moment';

import './ManageSubscription.scss';

const PaddleLifetimeUpgrade = ({ modalId, onClose }) => {
  const dispatch = useDispatch();
  const account = useSelector(({ Account }) => Account.data);
  const pricingPlans = useCallback(getPricing(), []);
  const selectedPlan = pricingPlans.lifetimePlusInitial;
  const [eventId, setEventId] = useState(Storage.get('subscriptionEventId'));
  const [totals, setTotals] = useState({
    subtotal: selectedPlan.price,
    tax: 0,
    total: selectedPlan.price,
    coupon: null,
    currency: 'USD',
  });
  const [provisioning, setProvisioning] = useState(false);
  const pollingInterval = useRef(null);
  const [loading, setLoading] = useState(false);
  const [pollingTime, setPollingTime] = useState(10000);
  const platformNames = {
    ios: 'Apple',
    android: 'Google Play',
  };

  const { openCheckout } = usePaddleCheckout({
    onEvent: event => {
      console.log('PaddleLifetimeUpgrade', 'event', event.name, event.data);

      // Update totals when we receive pricing events
      if (event.name && event.data?.recurring_totals) {
        const eventData = event.data;
        setTotals({
          subtotal: eventData.recurring_totals.subtotal,
          tax: eventData.recurring_totals.tax,
          total: eventData.recurring_totals.total,
          currency: eventData.currency_code,
          coupon: eventData.recurring_totals.discount
            ? {
                description: eventData.discount.code,
                amount: eventData.recurring_totals.discount,
              }
            : null,
        });
      }

      // Handle checkout completion
      if (event.name === 'checkout.completed') {
        // Show loading state and poll for provisioning status
        setProvisioning(true);
        setPollingTime(1000);
      }
    },
    settings: {
      displayMode: 'inline',
      frameTarget: 'checkout-container',
      frameInitialHeight: '450',
      frameStyle: 'width: 100%; min-width: 312px; background-color: transparent; border: none;',
      variant: 'one-page',
    },
  });

  useEffect(() => {
    console.log('PaddleLifetimeUpgrade', 'useEffect', pollingInterval.current);
    pollingInterval.current = setInterval(async () => {
      console.log('PaddleLifetimeUpgrade', 'polling for subscription');
      const { data: apiUser } = await UserApi.fetch();

      const newSubscription = apiUser.subscriptions.find(({ platform, created_at }) => {
        return platform === 'paddle' && moment(created_at).isAfter(moment().subtract(1, 'minutes'));
      });

      if (newSubscription) {
        console.log('PaddleLifetimeUpgrade', 'clearing polling interval');
        dispatch(setUser(apiUser));
        clearInterval(pollingInterval.current);
        onClose(true);
      }
    }, pollingTime);

    return () => {
      console.log('PaddleLifetimeUpgrade', 'useEffect release', pollingInterval.current);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [dispatch, pollingTime, onClose, pollingInterval]);

  useMountEffect(() => {
    if (!eventId) {
      const newId = guid();
      Storage.set('subscriptionEventId', newId);
      setEventId(newId);
    }

    async function getPaddleData() {
      try {
        setLoading(true);
        const { data: paddleData } = await paddleApi.subscribe({
          price: selectedPlan.commonIdentifier,
          allow_existing: true,
        });

        openCheckout({
          transactionId: paddleData.transaction_id,
          customerAuthToken: paddleData.customer_auth_token,
          customer: {
            id: paddleData.customer_id,
          },
          customData: {
            eventId,
          },
        });
        setLoading(false);
      } catch (error) {
        console.error('Failed to initialize Paddle checkout:', error);
        dispatch(showError('Failed to initialize checkout. Please try again later.'));
      }
    }

    getPaddleData();

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  });

  const renderSubscriptionWarning = () => {
    if (!account?.iapSubscription) return null;

    return (
      <p className="text-center">
        Be sure to cancel your existing{' '}
        {platformNames[account?.iapSubscription?.platform] || 'Apple/Google'} subscription to avoid
        future charges.
      </p>
    );
  };

  return (
    <>
      <h1>Upgrade to Lifetime Access</h1>
      <p className="sub-header">(Includes Artful Plus Features)</p>
      <p>
        Upgrade now to begin your lifetime access. Artful Plus features will be unlocked
        immediately!
      </p>
      {renderSubscriptionWarning()}
      <div className="checkout-wrapper">
        <div className="checkout-container" style={{ height: loading ? '450px' : null }} />
        <PaddleTotalWithTax {...totals} />
        {(provisioning || loading) && (
          <span className="spinner-wrapper">
            <Spinner />
          </span>
        )}
      </div>
      <input id="artfulUserId" type="hidden" name="userId" value={account?.id} />
      <input id="artfulEventId" type="hidden" name="eventId" value={eventId} />
    </>
  );
};

PaddleLifetimeUpgrade.propTypes = {
  modalId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default PaddleLifetimeUpgrade;
